import { getSelectionContext, offsetToSourceRange } from "./source";
import type { AnnotationStatus, PreviewAnnotation, SourceTextAnnotationTarget } from "./types";

export interface RelocateSourceTextAnnotationsOptions {
    documentPath: string;
    content: string;
    annotations: PreviewAnnotation[];
    now?: string;
}

export interface RelocateSourceTextAnnotationsResult {
    annotations: PreviewAnnotation[];
    changed: boolean;
}

interface MatchCandidate {
    startOffset: number;
    score: number;
}

export function relocateSourceTextAnnotations({
    documentPath,
    content,
    annotations,
    now = new Date().toISOString(),
}: RelocateSourceTextAnnotationsOptions): RelocateSourceTextAnnotationsResult {
    let changed = false;
    const nextAnnotations = annotations.map((annotation) => {
        const target = annotation.target;
        if (target.kind !== "source-text" || target.documentPath !== documentPath) {
            return annotation;
        }

        const quote = target.quote;
        if (!quote) return annotation;

        const currentSlice = content.slice(target.startOffset, target.endOffset);
        if (currentSlice === quote) {
            const range = offsetToSourceRange(content, target.startOffset, target.endOffset);
            const context = getSelectionContext(content, target.startOffset, target.endOffset);
            const status = restoreStatus(annotation);
            const nextTarget = {
                ...target,
                range,
                prefix: context.prefix,
                suffix: context.suffix,
            };
            if (
                status === annotation.status &&
                !annotation.statusBeforeOrphaned &&
                sourceTargetEqual(target, nextTarget)
            ) {
                return annotation;
            }
            changed = true;
            return {
                ...annotation,
                status,
                statusBeforeOrphaned: undefined,
                target: nextTarget,
                updatedAt: now,
            };
        }

        const relocatedStart = findRelocatedStart(content, target);
        if (relocatedStart == null) {
            if (annotation.status === "orphaned") return annotation;
            changed = true;
            return {
                ...annotation,
                status: "orphaned" as const,
                statusBeforeOrphaned: annotation.status,
                updatedAt: now,
            };
        }

        const relocatedEnd = relocatedStart + quote.length;
        const context = getSelectionContext(content, relocatedStart, relocatedEnd);
        const nextTarget: SourceTextAnnotationTarget = {
            ...target,
            range: offsetToSourceRange(content, relocatedStart, relocatedEnd),
            startOffset: relocatedStart,
            endOffset: relocatedEnd,
            quote: context.quote || quote,
            prefix: context.prefix,
            suffix: context.suffix,
        };
        const status = restoreStatus(annotation);
        if (
            status === annotation.status &&
            !annotation.statusBeforeOrphaned &&
            sourceTargetEqual(target, nextTarget)
        ) {
            return annotation;
        }
        changed = true;
        return {
            ...annotation,
            status,
            statusBeforeOrphaned: undefined,
            target: nextTarget,
            updatedAt: now,
        };
    });

    return { annotations: nextAnnotations, changed };
}

function restoreStatus(annotation: PreviewAnnotation): AnnotationStatus {
    if (annotation.status !== "orphaned") return annotation.status;
    return annotation.statusBeforeOrphaned ?? "open";
}

function findRelocatedStart(
    content: string,
    target: SourceTextAnnotationTarget,
): number | null {
    const quote = target.quote;
    const candidates: MatchCandidate[] = [];
    let cursor = 0;
    while (cursor <= content.length) {
        const found = content.indexOf(quote, cursor);
        if (found < 0) break;
        candidates.push({
            startOffset: found,
            score: contextScore(content, found, found + quote.length, target),
        });
        cursor = found + Math.max(1, quote.length);
    }
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0]?.startOffset ?? null;

    candidates.sort((a, b) => b.score - a.score);
    const best = candidates[0];
    const second = candidates[1];
    if (!best || !second || best.score === 0 || best.score === second.score) {
        return null;
    }
    return best.startOffset;
}

function contextScore(
    content: string,
    startOffset: number,
    endOffset: number,
    target: SourceTextAnnotationTarget,
): number {
    const prefix = target.prefix ?? "";
    const suffix = target.suffix ?? "";
    const before = content.slice(Math.max(0, startOffset - prefix.length), startOffset);
    const after = content.slice(endOffset, Math.min(content.length, endOffset + suffix.length));
    return commonSuffixLength(prefix, before) + commonPrefixLength(suffix, after);
}

function commonPrefixLength(a: string, b: string): number {
    const length = Math.min(a.length, b.length);
    let count = 0;
    while (count < length && a[count] === b[count]) count += 1;
    return count;
}

function commonSuffixLength(a: string, b: string): number {
    const length = Math.min(a.length, b.length);
    let count = 0;
    while (
        count < length &&
        a[a.length - count - 1] === b[b.length - count - 1]
    ) {
        count += 1;
    }
    return count;
}

function sourceTargetEqual(
    left: SourceTextAnnotationTarget,
    right: SourceTextAnnotationTarget,
): boolean {
    return (
        left.startOffset === right.startOffset &&
        left.endOffset === right.endOffset &&
        left.quote === right.quote &&
        left.prefix === right.prefix &&
        left.suffix === right.suffix &&
        left.range.startLine === right.range.startLine &&
        left.range.startColumn === right.range.startColumn &&
        left.range.endLine === right.range.endLine &&
        left.range.endColumn === right.range.endColumn
    );
}
